import { useId } from 'react';
import { campaignCopy } from '@discovr/features';
import { Textarea } from '../../components/ui/textarea';
import {
  HOLD_NOTE_ANNOUNCE_AT,
  HOLD_NOTE_INPUT_MAX_UNITS,
  HOLD_NOTE_MAX,
  holdNoteLength,
} from './ConfirmedTab.holdReasons';

const copy = campaignCopy.creator.hold.note;

interface Props {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  /** Announced as an alert and marks the field invalid. */
  error: string | null;
  /** Plain helper text shown when there is no error. */
  hint?: string | null;
  /** null renders no placeholder. */
  placeholder?: string | null;
  required?: boolean;
  disabled?: boolean;
}

export function HoldNoteField({
  label,
  value,
  onChange,
  onBlur,
  error,
  hint,
  placeholder = copy.placeholder,
  required,
  disabled,
}: Props) {
  const fieldId = useId();
  const messageId = useId();
  const remaining = HOLD_NOTE_MAX - holdNoteLength(value);
  const nearLimit = remaining >= 0 && remaining <= HOLD_NOTE_ANNOUNCE_AT;
  const message = error ?? hint ?? null;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={fieldId} className="text-sm font-medium">
        {label}
      </label>
      <Textarea
        id={fieldId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        maxLength={HOLD_NOTE_INPUT_MAX_UNITS}
        rows={4}
        placeholder={placeholder ?? undefined}
        disabled={disabled}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={message ? messageId : undefined}
      />
      <span className="self-end text-xs text-muted-foreground">
        {holdNoteLength(value)}/{HOLD_NOTE_MAX}
      </span>
      <span aria-live="polite" className="sr-only">
        {nearLimit ? copy.remaining(remaining) : ''}
      </span>
      {error ? (
        <p id={messageId} role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : hint ? (
        <p id={messageId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
